import { IUser } from "@models/models";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { identifySession } from "@utils/analytics";
import { ERoutes, POST_LOGIN_REDIRECT_KEY } from "@utils/constants";

import {
  IError,
  ILogin,
  ILogout,
  IRegister,
  IResetPassword,
  IUpdateEmail,
  IUpdatePassword,
} from "@/models/inner-models";

import { EUser } from "../constants";
import instance from "../middleware/api";

const my = createAsyncThunk<IUser>(EUser.getMy, async (_, thunkAPI) => {
  try {
    const resp = await instance.get("/users/my");

    if (!resp?.data) {
      return thunkAPI.rejectWithValue({ error: resp.data.result.message });
    }

    // Link this device's anonymous session to the user (visit→signup). No-ops
    // without tracking consent, and self-limits to once per browser session.
    // Backend only stamps signupAt on the first attribution.
    identifySession();

    return resp.data;
  } catch (err) {
    console.log("error", err);
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const updateMy = createAsyncThunk(EUser.patchMy, async (data: Record<string, unknown>, thunkAPI) => {
  try {
    const resp = await instance.patch("/users/my", data);

    if (!resp?.data) {
      return thunkAPI.rejectWithValue({ error: resp.data.result.message });
    }

    return resp.data;
  } catch (err) {
    console.log("error", err);
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

/** Step 1 of the two-step account deletion flow: send a 6-digit code
    to the user's verified email. The actual deletion does NOT happen
    here — verifyAccountDelete handles that after the code lands. */
const startAccountDelete = createAsyncThunk(EUser.accountDeleteStart, async (_: void, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/delete-account/start");
    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

/** Step 2: validate the code AND remove the account row in one call.
    Server captures email + firstName before deletion so the
    `account_deleted` confirmation email can fire via sendToAddress. The
    frontend follows up with a logout + redirect to /account-deleted. */
const verifyAccountDelete = createAsyncThunk(
  EUser.accountDeleteVerify,
  async (data: { code: string }, thunkAPI) => {
    try {
      const resp = await instance.post("/users/my/delete-account/verify", data);
      return resp?.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const cancelAccountDelete = createAsyncThunk(EUser.accountDeleteCancel, async (_: void, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/delete-account/cancel");
    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

/** Day 0 → 30 restore endpoint. The user has logged in successfully
    (LoginDialog handles the ACCOUNT_SCHEDULED_FOR_DELETION code by
    stashing the returned tokens + showing a blocking restore modal).
    On confirm, this thunk clears deletedAt + permanentDeleteScheduledAt
    server-side and the user can continue normally. */
const restoreAccountFromDeletion = createAsyncThunk(
  "auth/restore-from-deletion",
  async (_: void, thunkAPI) => {
    try {
      const resp = await instance.post("/users/my/restore-from-deletion");
      return resp?.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const sendVerificationCode = createAsyncThunk(EUser.sendVerificationCode, async (_, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/send-verification-code");

    if (!resp?.data) {
      return thunkAPI.rejectWithValue({ error: resp.data.result.message });
    }

    return resp.data;
  } catch (err) {
    console.log("error", err);
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const verifyCode = createAsyncThunk(EUser.verifyCode, async (data: Record<string, unknown>, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/verify-code", data);

    if (!resp?.data) {
      return thunkAPI.rejectWithValue({ error: resp.data.result.message });
    }

    return resp.data;
  } catch (err) {
    console.log("error", err);
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const refreshToken = createAsyncThunk(
  EUser.refreshToken,
  async (_: void, thunkAPI) => {
    try {
      // The refresh token rides along as the HttpOnly `as_refresh` cookie
      // (withCredentials on the axios instance) — nothing to read/send here.
      const resp = await instance.post("/auth/refresh-session");

      return resp?.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue(error);
    }
  },
);
const login = createAsyncThunk(EUser.loginUser, async (loginInfo: ILogin, thunkAPI) => {
  try {
    const resp = await instance.post("/auth/login", loginInfo);

    // Return to wherever a guard bounced them from (a licence email link, say),
    // else the catalog. Consumed once so a later plain login goes to catalog.
    let dest: string = ERoutes.Catalog;
    try {
      const back = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (back) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        dest = back;
      }
    } catch { /* storage blocked — catalog */ }
    window.location.href = dest;

    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const register = createAsyncThunk(EUser.registerUser, async (registerInfo: IRegister | ILogin, thunkAPI) => {
  try {
    const resp = await instance.post("/auth/register", registerInfo);

    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const logout = createAsyncThunk(EUser.logOutUser, async (logoutInfo: ILogout, thunkAPI) => {
  try {
    const resp = await instance.post("/auth/logout", logoutInfo);

    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const updatePassword = createAsyncThunk(EUser.updatePassword, async (data: IUpdatePassword, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/change-password", data);

    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const updateEmail = createAsyncThunk(EUser.updateEmail, async (data: IUpdateEmail, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/change-email", data);

    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const sendVerificationLink = createAsyncThunk(
  EUser.sendVerificationLink,
  async (email: Record<string, unknown>, thunkAPI) => {
    try {
      const resp = await instance.post("/auth/send-verification-link-password", email);

      return resp?.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const validateResetToken = createAsyncThunk(
  EUser.validateResetToken,
  async (resetToken: string, thunkAPI) => {
    try {
      const resp = await instance.post("/auth/reset-password/validate", { resetToken });
      return resp?.data as { valid: boolean };
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const resetPassword = createAsyncThunk(EUser.resetPassword, async (newPassword: IResetPassword, thunkAPI) => {
  try {
    const resp = await instance.patch("/auth/reset-password", newPassword);

    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const startPasswordChange = createAsyncThunk(EUser.passwordChangeStart, async (_: void, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/password-change/start");
    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const verifyPasswordChange = createAsyncThunk(
  EUser.passwordChangeVerify,
  async (data: { code: string }, thunkAPI) => {
    try {
      const resp = await instance.post("/users/my/password-change/verify", data);
      return resp?.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const commitPasswordChange = createAsyncThunk(
  EUser.passwordChangeCommit,
  async (data: { newPassword: string }, thunkAPI) => {
    try {
      const resp = await instance.post("/users/my/password-change/commit", data);
      return resp?.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const cancelPasswordChange = createAsyncThunk(EUser.passwordChangeCancel, async (_: void, thunkAPI) => {
  try {
    const resp = await instance.post("/users/my/password-change/cancel");
    return resp?.data;
  } catch (err) {
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

const captcha = createAsyncThunk(EUser.captcha, async (token: string, thunkAPI) => {
  try {
    const resp = await instance.post("/captcha", { token });

    return resp.data;
  } catch (err) {
    console.log("error", err);
    const error = err as IError;
    return thunkAPI.rejectWithValue(error);
  }
});

export {
  login,
  register,
  logout,
  refreshToken,
  my,
  updateMy,
  sendVerificationCode,
  verifyCode,
  sendVerificationLink,
  updatePassword,
  updateEmail,
  resetPassword,
  validateResetToken,
  startPasswordChange,
  verifyPasswordChange,
  commitPasswordChange,
  cancelPasswordChange,
  startAccountDelete,
  verifyAccountDelete,
  cancelAccountDelete,
  restoreAccountFromDeletion,
  captcha,
};
